// How long a voice usually takes, from how much audio it trains on.
//
// Measured, not modelled: minutes from submit to the "trained" notification
// for 2,103 voices that finished between 2026-09-09 and 2026-10-09 (farm
// accounts excluded), bucketed by the seconds the job was sent. Repeats were
// trained like any other file then, so that is what drove the time; now that
// /train doesn't add repeats and startTrainingFull drops repeated URLs, the
// seconds sent are the unique seconds. `typical` is the median, `within` the
// 80th percentile - "usually ready within" should be true four times in five.
// The training release in test (faster loop, MelBandRoformer) will move
// these; re-measure from models.trainingStats.timings once it ships.
const TABLE: { upTo: number; typical: number; within: number }[] = [
  { upTo: 10 * 60, typical: 23, within: 30 },
  { upTo: 20 * 60, typical: 37, within: 47 },
  { upTo: 30 * 60, typical: 53, within: 75 },
  { upTo: 45 * 60, typical: 71, within: 95 },
  { upTo: 61 * 60, typical: 107, within: 143 },
  { upTo: Infinity, typical: 121, within: 169 },
];

export function trainingEta(seconds: number | null | undefined) {
  if (!seconds || !Number.isFinite(seconds) || seconds < 0) return null;
  const row = TABLE.find((r) => seconds <= r.upTo)!;
  return { typicalMin: row.typical, withinMin: row.within };
}

/** "about 25 minutes", "about 1 hour 50 minutes" */
export function minutesLabel(min: number) {
  const m = Math.round(min / 5) * 5 || 5;
  if (m < 60) return `about ${m} minutes`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `about ${h} hour${h > 1 ? "s" : ""}${r ? ` ${r} minutes` : ""}`;
}

// Past this a voice still marked training is rarely coming back on its own
// (99% of the voices above finished within 4 hours, 12 of 2,103 took over 6;
// 400+ old voices carry `training: true` from before the webhook cleared
// it). The library stops estimating and stops polling for it.
export const TRAINING_GIVE_UP_MIN = 6 * 60;

/**
 * Where a training voice usually is by now: elapsed time against the time
 * for this much audio. `pct` is capped at 95 so the bar never says done
 * before the webhook does. null when there is nothing honest to show (no
 * length recorded, or long past any normal finish).
 */
export function trainingProgress(
  seconds: number | null | undefined,
  startedAt: string | number | Date | null | undefined,
  now = Date.now(),
) {
  const eta = trainingEta(seconds);
  const start = startedAt ? new Date(startedAt).getTime() : NaN;
  if (!eta || !Number.isFinite(start)) return null;
  const elapsedMin = Math.max(0, (now - start) / 60000);
  if (elapsedMin > TRAINING_GIVE_UP_MIN) return null;
  return {
    pct: Math.min(95, Math.round((elapsedMin / eta.withinMin) * 100)),
    late: elapsedMin >= eta.withinMin,
    leftLabel: minutesLabel(Math.max(5, eta.typicalMin - elapsedMin)),
  };
}

/** A voice the library should keep refreshing for (see TRAINING_GIVE_UP_MIN). */
export function isFreshTraining(
  model: {
    training?: boolean;
    failed?: boolean;
    createdAt?: string;
    trainingStartedAt?: string;
  } | null,
  now = Date.now(),
) {
  if (!model?.training || model.failed) return false;
  const started = trainingStartOf(model);
  const start = started ? new Date(started).getTime() : NaN;
  return (
    Number.isFinite(start) && now - start < TRAINING_GIVE_UP_MIN * 60 * 1000
  );
}

/** When the current run began: a retry's dispatch, else when it was made. */
export function trainingStartOf(
  model: { createdAt?: string; trainingStartedAt?: string } | null,
) {
  return model?.trainingStartedAt ?? model?.createdAt;
}
